'use client'

import {FormContext} from '@local/stores/contexts'
import {cx, tv} from '@local/utils/cx'
import {InputHTMLAttributes, useContext, ReactNode, RefObject, useRef, useState, useEffect, useMemo, forwardRef, useImperativeHandle} from 'react'

import {VariantProps} from 'tailwind-variants'
import {makeErrors} from '@local/utils/error'

export const inputOrTextAreaStyles = tv({
	slots: {
		base: cx(
			'peer',
			'flex bg-transparent focus:outline-none duration-300 ease-quart-out transition-colors',
			'text-14 leading-narrow tracking-tighter',
			'placeholder:text-current',
			'border rounded-xs px-16 h-44',
			'text-neutral-100',
		),
		label: [
			'order-first text-12 tracking-tight leading-none duration-300 ease-quart-out',
			'absolute top-0 transform -translate-y-1/2 z-2 origin-left peer-focus:top-0 pointer-events-none',
			'bg-neutral-0',
		],
		error: [
			'absolute top-full',
			'text-9 font-mono text-red duration-300 ease-quart-out transition-opacity pointer-events-none',
			'bg-neutral-0 opacity-0 peer-invalid:opacity-100',
		],
	},
	variants: {
		disabled: {
			true: {
				base: 'cursor-not-allowed opacity-60',
			},
		},
		preserveNativeAppearance: {
			true: {
				base: 'appearance-none',
			},
			false: {
				label: 'top-1/2 py-3',
			},
		},
		isTextArea: {
			true: {
				base: 'h-100 py-12 resize-none',
				label: 'peer-placeholder-shown:top-18',
				error: 'left-16',
			},
			false: {
				label: 'peer-placeholder-shown:top-1/2',
			},
		},
		variant: {
			dark: {
				base: [
					'text-white-off placeholder:opacity-100 placeholder:text-white-off',
					'border-grey-2 focus:border-white-off',
				],
				label: [
					'bg-grey-5 text-white-off peer-focus:text-white-off',
				],
				error: [
					'bg-grey-5',
				],
			},
			light: {
				base: [
					'placeholder:text-black',
					'border-black focus:border-black',
				],
				label: [
					'bg-white-off text-grey-5',
				],
				error: [
					'bg-white-off',
				],
			},
			box: {
				base: [
					'p-10 border rounded-[1px] h-48',
					'border border-neutral-100/20 focus:border-neutral-100',
				],
				label: [
					'top-0 peer-placeholder-shown:top-1/2',
				],
			},
		},
		hasValidation: {
			true: {
				base: [
					'invalid:text-red invalid:border-red invalid:focus:border-red-light invalid:active:border-red-light',
				],
				label: [
					'peer-invalid:text-red peer-invalid:peer-focus:text-red-light',
				],
				error: [
					'peer-invalid:opacity-100 absolute top-full',
				],
			},
		},
		hasError: {
			true: {
				base: [
					'!border-red text-red',
				],
				label: [
					'!text-red',
				],
				error: [
					'peer-invalid:opacity-100 absolute top-full',
				],
			},
		},
		hasValue: {
			true: {
				label: [
					'top-0',
				],
			},
		},
	},
})

type InputOrTextAreaVariants = VariantProps<typeof inputOrTextAreaStyles>

export interface InputProps extends InputHTMLAttributes<HTMLInputElement & HTMLTextAreaElement> {
	children?: ReactNode,
	name: string,
	label?: string,
	placeholder?: string,
	id?: string,
	inputClassName?: string,
	errorClassName?: string,
	labelClassName?: string,
	required?: boolean,
	errorSpace?: boolean
	floatLabel?: boolean,
	floatError?: boolean,
	errored?: boolean,
	errorMessage?: string,
	formatter?: (value: string) => string
	validator?: (input, ref) => {isValid: boolean, errorMessage?: string}
	exampleValidInput?: string
	disableRequiredIndicator?: boolean
	customErrors?: any
	eagerValidation?: boolean
	variant?: 'box' | 'dark' | 'light'
	hasError?: boolean
}
export interface InputOrTextAreaProps extends InputProps, InputOrTextAreaVariants {
	as: 'input' | 'textarea'
}

export const InputOrTextArea = forwardRef(
	(
		{
			type,
			as,
			id,
			children,
			name,
			className,
			inputClassName,
			errorClassName,
			labelClassName,
			defaultValue,
			label,
			placeholder,
			required = false,
			floatLabel = true,
			floatError = true,
			variant = 'box',
			hasError = false,
			errorSpace,
			disableRequiredIndicator = false,
			errorMessage: _errorMessage,
			exampleValidInput,
			formatter,
			validator,
			customErrors = {},
			onChange,
			eagerValidation,
			...props
		}: InputOrTextAreaProps,
		ref,
	) => {
		const {hasValidation} = useContext(FormContext)
		const localRef = useRef<HTMLInputElement|HTMLTextAreaElement>(null)

		const [localValue, setLocalValue] = useState(defaultValue || '')

		const preserveNativeAppearance = useMemo(() => ['date', 'time'].includes(type || ''), [type])
		const getError = makeErrors(exampleValidInput ? {patternMismatch: 'should be of format', exampleValidInput, ...customErrors} : customErrors)
		const needsError = (hasValidation || (eagerValidation && localValue))
		const localErrorMessage = needsError && localRef.current && !localRef.current.validity?.valid && getError(localRef.current)
		const errorMessage = needsError && (_errorMessage || localErrorMessage)

		const Tag = as
		const isTextArea = Tag === 'textarea'

		if (placeholder && required && !disableRequiredIndicator) placeholder += '*'
		if (floatLabel && !label && placeholder) label = placeholder

		const checkValidity = (value = localValue) => {
			if (typeof validator === 'function') {
				const {isValid, errorMessage} = validator(value, localRef.current)
				if (localRef.current) {
					localRef.current.setCustomValidity?.((isValid || !errorMessage) ? '' : errorMessage)
				}
			}
		}

		const handleChange = (e) => {
			const value = e.target.value
			const formattedValue = formatter ? formatter(value) : value
			e.formattedValue = formattedValue
			setLocalValue(formattedValue)
			checkValidity(formattedValue)
			onChange?.(e)
		}

		useEffect(checkValidity, [validator])

		const {base: baseClassName, label: baseLabelClassName, error: baseErrorClassName} = inputOrTextAreaStyles({
			variant,
			preserveNativeAppearance,
			isTextArea,
			disabled: props.disabled,
			hasValidation,
			hasError,
			hasValue: Boolean(localValue),
		})

		useImperativeHandle(ref, () => {
			return {
				node: localRef.current,
				blur: () => localRef.current?.blur(),
				reset: () => setLocalValue(''),
			}
		})

		return (
			<div className={cx(
				className,
				'relative flex flex-col',
				((!floatError && errorMessage) || (errorSpace)) && 'mb-18',
			)}>
				<Tag
				// type={shouldReplaceType ? 'text' : type}
					type={type}
					value={localValue}
					name={name}
					id={id || name}
					ref={(node) => {
						localRef.current = node
						if (ref) {
							ref.current = node
						}
					}}
					required={required}
					placeholder={floatLabel ? ' ' : placeholder}
					aria-label={label}
					onChange={handleChange}
					className={cx(
						baseClassName(),
						inputClassName,
					)}
					{...props} />
				{label &&
					<label
						htmlFor={id || name}
						className={cx(
							floatLabel ? 'absolute top-0 ml-5 px-5 -translate-y-1/2 z-2 origin-left peer-focus:top-0 pointer-events-none left-0 mr-1' : 'pb-4',
							preserveNativeAppearance && localValue && 'top-0',
							baseLabelClassName(),
							labelClassName,
						)}>
						{label}
					</label>}
				{(errorMessage) &&
					<span
						className={cx(
							baseErrorClassName(),
							floatError ? 'px-5 ml-5 -translate-y-1/2' : 'pt-5 left-0',
							errorClassName,
						)}>
						{errorMessage}
					</span>}
				{children}
			</div>
		)
	},
)
